import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import useLogin from "../../api/login";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import {
  Eye,
  EyeOff,
  AlertCircle,
  BarChart3,
  Mail,
  Package,
  Sun,
  Moon,
} from "lucide-react";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const { login: authLogin, company, loading } = useAuth();
  const { login } = useLogin();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";

  // Redirect to dashboard if already authenticated (e.g. "remember me")
  useEffect(() => {
    if (!loading && company) {
      navigate("/dashboard", { replace: true });
    }
  }, [loading, company, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const { token, company } = await login(email, password);
      await authLogin(token, company, rememberMe);
      navigate("/dashboard");
    } catch (err) {
      console.error("Login failed:", err);
      setError(
        err.response?.data?.message ||
          "Login mislukt. Controleer uw e-mailadres en wachtwoord.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Show nothing while checking auth to avoid login form flash
  if (loading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${isDark ? "bg-[#0a0a0a]" : "bg-[#f8f8fa]"}`}
      >
        <div
          className={`h-8 w-8 rounded-full border-2 animate-spin ${isDark ? "border-white/20 border-t-white/60" : "border-gray-200 border-t-gray-600"}`}
        />
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen flex transition-colors duration-200 ${isDark ? "bg-[#0a0a0a]" : "bg-[#f8f8fa]"}`}
    >
      {/* Left — Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6">
        <div className="w-full max-w-[400px]">
          {/* Logo + theme toggle */}
          <div className="flex items-center justify-between mb-10">
            <Link to="/" className="flex items-center">
              <span
                className={`text-[15px] font-zichtbaar font-black tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
              >
                ZICHTBAAR
              </span>
            </Link>
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-colors ${isDark ? "text-gray-500 hover:text-gray-300 hover:bg-white/[0.04]" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
            >
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            {/* Header */}
            <div>
              <h2
                className={`text-xl font-semibold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
              >
                Welkom terug
              </h2>
              <p
                className={`text-[13px] mt-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                Log in om toegang te krijgen tot uw dashboard
              </p>
            </div>

            {/* Error */}
            {error && (
              <div
                className={`flex items-start gap-2 px-3 py-2.5 rounded-lg text-[12px] ${isDark ? "bg-red-500/10 border border-red-500/20 text-red-400" : "bg-red-50 border border-red-200 text-red-700"}`}
              >
                <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label
                  htmlFor="email"
                  className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="uw@bedrijf.be"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={`w-full h-10 px-3 rounded-lg text-[13px] outline-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.08] text-white placeholder-gray-600 focus:border-white/[0.15]" : "bg-white border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label
                    htmlFor="password"
                    className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
                  >
                    Wachtwoord
                  </label>
                  <Link
                    to="/forgot-password"
                    className={`text-[11px] font-medium transition-colors ${isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
                  >
                    Vergeten?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className={`w-full h-10 px-3 pr-10 rounded-lg text-[13px] outline-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.08] text-white placeholder-gray-600 focus:border-white/[0.15]" : "bg-white border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${isDark ? "text-gray-600 hover:text-gray-400" : "text-gray-400 hover:text-gray-600"}`}
                  >
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              {/* Remember me */}
              <label className="flex items-center gap-2 cursor-pointer group">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div
                    className={`w-4 h-4 rounded border transition-colors peer-checked:bg-blue-600 peer-checked:border-blue-600 ${isDark ? "border-white/[0.15] bg-white/[0.04]" : "border-gray-300 bg-white"}`}
                  />
                  <svg
                    className="absolute top-0.5 left-0.5 w-3 h-3 text-white hidden peer-checked:block pointer-events-none"
                    viewBox="0 0 12 12"
                    fill="none"
                  >
                    <path
                      d="M2 6l3 3 5-5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span
                  className={`text-[12px] font-medium ${isDark ? "text-gray-500 group-hover:text-gray-300" : "text-gray-500 group-hover:text-gray-700"}`}
                >
                  Onthoud mij
                </span>
              </label>

              <button
                type="submit"
                disabled={isLoading}
                className={`w-full h-10 rounded-lg text-[13px] font-medium transition-all duration-150 ${isLoading ? "opacity-70 cursor-not-allowed" : ""} ${isDark ? "bg-[#1E4FCF] text-white hover:bg-[#2a5fd9]" : "bg-[#BFFF00] text-black hover:bg-[#d4ff4d]"}`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div
                      className={`h-3.5 w-3.5 rounded-full border-2 animate-spin ${isDark ? "border-white/20 border-t-white/60" : "border-black/20 border-t-black/60"}`}
                    />
                    <span>Inloggen...</span>
                  </div>
                ) : (
                  "Inloggen"
                )}
              </button>
            </form>

            <p
              className={`text-center text-[12px] ${isDark ? "text-gray-600" : "text-gray-500"}`}
            >
              Nog geen account?{" "}
              <a
                href="https://zichtbaarstudio.be/contact"
                target="_blank"
                rel="noopener noreferrer"
                className={`font-medium transition-colors ${isDark ? "text-gray-400 hover:text-white" : "text-gray-700 hover:text-gray-900"}`}
              >
                Contacteer support
              </a>
            </p>
          </motion.div>

          <div
            className={`mt-10 text-center text-[11px] ${isDark ? "text-gray-700" : "text-gray-400"}`}
          >
            <p>&copy; {new Date().getFullYear()} Zichtbaar Dashboard</p>
            <div className="mt-1 flex items-center justify-center gap-2">
              <Link
                to="/privacy"
                className={`transition-colors ${isDark ? "text-gray-600 hover:text-gray-400" : "text-gray-400 hover:text-gray-600"}`}
              >
                Privacybeleid
              </Link>
              <span className={isDark ? "text-gray-800" : "text-gray-300"}>·</span>
              <Link
                to="/terms"
                className={`transition-colors ${isDark ? "text-gray-600 hover:text-gray-400" : "text-gray-400 hover:text-gray-600"}`}
              >
                Gebruiksvoorwaarden
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Right — Preview panel */}
      <div
        className={`hidden lg:flex lg:w-1/2 relative overflow-hidden ${isDark ? "bg-[#050510]" : "bg-[#1E4FCF]"}`}
      >
        {/* Animated blue glow orbs — dark mode */}
        {isDark && (
          <>
            <motion.div
              className="absolute w-[600px] h-[600px] rounded-full opacity-[0.15] blur-[120px] pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle, #1E4FCF 0%, transparent 70%)",
                top: "-10%",
                left: "-10%",
              }}
              animate={{
                x: [0, 120, -40, 0],
                y: [0, -80, 80, 0],
                scale: [1, 1.15, 0.9, 1],
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <motion.div
              className="absolute w-[400px] h-[400px] rounded-full opacity-[0.12] blur-[100px] pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle, #3b82f6 0%, transparent 70%)",
                bottom: "-5%",
                right: "-5%",
              }}
              animate={{
                x: [0, -100, 60, 0],
                y: [0, 60, -100, 0],
                scale: [1, 0.85, 1.2, 1],
              }}
              transition={{
                duration: 14,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <motion.div
              className="absolute w-[250px] h-[250px] rounded-full opacity-[0.1] blur-[80px] pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle, #6366f1 0%, transparent 70%)",
                top: "40%",
                left: "30%",
              }}
              animate={{
                x: [0, 60, -80, 0],
                y: [0, -60, 40, 0],
                scale: [1, 1.3, 0.8, 1],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </>
        )}
        <div className="absolute inset-0 flex flex-col items-center justify-center p-12 z-10">
          <div className="max-w-md text-center mb-8">
            <h2 className="text-2xl font-semibold text-white mb-3">
              Uw dashboard, altijd bereikbaar
            </h2>
            <p className="text-[14px] text-white/60">
              Beheer berichten, bekijk statistieken en houd alles overzichtelijk
              vanuit één plek.
            </p>
          </div>

          {/* Mini dashboard preview */}
          <div
            className={`w-full max-w-sm rounded-xl overflow-hidden shadow-2xl ${isDark ? "bg-[#0a0a0a] border border-white/[0.06]" : "bg-white"}`}
          >
            {/* Preview header */}
            <div
              className={`px-4 py-3 flex items-center justify-between border-b ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
            >
              <span
                className={`text-[11px] font-zichtbaar font-black tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
              >
                ZICHTBAAR{" "}
                <span className={isDark ? "text-gray-500" : "text-gray-400"}>
                  DASH
                </span>
              </span>
              <div className="flex items-center gap-1">
                <div
                  className={`h-2 w-2 rounded-full ${isDark ? "bg-emerald-500/50" : "bg-emerald-400"}`}
                />
                <span
                  className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-400"}`}
                >
                  Online
                </span>
              </div>
            </div>

            {/* Preview stat cards */}
            <div className="p-4 space-y-2">
              {[
                {
                  icon: Mail,
                  label: "Inbox",
                  value: "12 berichten",
                  dot: "bg-blue-500",
                },
                {
                  icon: BarChart3,
                  label: "Statistieken",
                  value: "1.2k bezoekers",
                  dot: "bg-emerald-500",
                },
                {
                  icon: Package,
                  label: "Items",
                  value: "38 actief",
                  dot: "bg-amber-500",
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg ${isDark ? "bg-white/[0.03]" : "bg-gray-50"}`}
                >
                  <div
                    className={`w-7 h-7 rounded-md flex items-center justify-center ${isDark ? "bg-white/[0.06]" : "bg-white"}`}
                  >
                    <item.icon
                      className={`h-3.5 w-3.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                    />
                  </div>
                  <div className="flex-1">
                    <p
                      className={`text-[11px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
                    >
                      {item.label}
                    </p>
                    <p
                      className={`text-[12px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                    >
                      {item.value}
                    </p>
                  </div>
                  <div className={`h-2 w-2 rounded-full ${item.dot}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
